"use client";

import { usePathname, useRouter } from "next/navigation";
import { useState, useRef, useEffect } from "react";
import { i18n, type Locale } from "@/i18n-config";
import { Settings, Monitor, Moon, Sun, Globe, Check } from "lucide-react";
import { ThemeName } from "./ThemeModeScript";
import { clsx } from "@/lib/utils";

export function SettingsMenu({
  currentLang,
  dict = {},
}: {
  currentLang: Locale;
  dict?: Record<string, string>;
}) {
  const pathName = usePathname();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [theme, setTheme] = useState<ThemeName>("system");

  useEffect(() => {
    setMounted(true);
    const SK = "meshflow.theme-preference";
    const s = window.localStorage;
    const pref = JSON.parse(s[SK] || "{}");
    if (pref.theme) {
      setTheme(pref.theme as ThemeName);
    }

    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);

    const handleStorage = (e: StorageEvent) => {
      if (e.key === SK) {
        const stored = JSON.parse(s[window.localStorage[SK] || "{}"]);
        if (stored?.theme) {
          setTheme(stored.theme as ThemeName);
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const languageNames: Record<string, string> = {
    en: "English",
    es: "Español",
    pl: "Polski",
  };

  const redirectedPathName = (locale: Locale) => {
    if (!pathName) return "/";
    const segments = pathName.split("/");
    const hasLocale = i18n.locales.includes(segments[1] as Locale);

    if (hasLocale) {
      if (locale === i18n.defaultLocale) {
        segments.splice(1, 1);
        const newPath = segments.join("/");
        return newPath || "/";
      } else {
        segments[1] = locale;
        return segments.join("/");
      }
    } else {
      if (locale === i18n.defaultLocale) {
        return pathName;
      } else {
        return `/${locale}${pathName === "/" ? "" : pathName}`;
      }
    }
  };

  const handleLangSelect = (locale: Locale) => {
    setIsOpen(false);
    if (locale === currentLang) return;
    router.push(redirectedPathName(locale));
  };

  const setThemeValue = (newTheme: ThemeName) => {
    setTheme(newTheme);
    const SK = "meshflow.theme-preference";
    const s = window.localStorage;
    const currentPrefs = JSON.parse(s.getItem(SK) || "{}");
    const newPrefsString = JSON.stringify({ ...currentPrefs, theme: newTheme });
    s.setItem(SK, newPrefsString);
    window.__themeRefresh?.();
  };

  const languageListClasses = clsx([
    "flex flex-col",
    // Base button styles
    "[&_button]:flex [&_button]:items-center [&_button]:justify-between [&_button]:w-full",
    "[&_button]:px-2 [&_button]:py-2 [&_button]:text-left [&_button]:rounded-md [&_button]:transition-colors",
    // Active / Pressed state
    "[&_button[aria-pressed='true']]:bg-primary/10 [&_button[aria-pressed='true']]:text-primary [&_button[aria-pressed='true']]:font-medium",
    // Inactive state hover and default
    "hover:[&_button:not([aria-pressed='true'])]:bg-muted/50 hover:[&_button:not([aria-pressed='true'])]:text-foreground",
    "[&_button:not([aria-pressed='true'])]:text-muted-foreground",
  ]);

  return (
    <div className="relative inline-block text-left" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-muted/50 transition-colors bg-transparent text-foreground"
        aria-label="Settings"
        aria-expanded={isOpen}
      >
        <Settings className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors" />
        <span className="text-sm font-medium uppercase">{currentLang}</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-xl bg-card border border-border shadow-lg p-2 flex flex-col gap-4 text-sm mt-4">
          {/* Theme Section */}
          <div>
            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
              Theme
            </div>
            <div className="flex bg-muted/30 p-1 rounded-lg border border-border">
              <button
                onClick={() => setThemeValue("light")}
                className={`flex-1 p-1.5 rounded-md transition-all flex items-center justify-center gap-1 ${theme === "light" ? "bg-background shadow-md text-foreground ring-1 ring-border dark:bg-muted-foreground/20 dark:ring-white/10" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"}`}
                aria-label="Light Theme"
              >
                <Sun className="w-4 h-4" />
              </button>
              <button
                onClick={() => setThemeValue("system")}
                className={`flex-1 p-1.5 rounded-md transition-all flex items-center justify-center gap-1 ${theme === "system" ? "bg-background shadow-md text-foreground ring-1 ring-border dark:bg-muted-foreground/20 dark:ring-white/10" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"}`}
                aria-label="System Theme"
              >
                <Monitor className="w-4 h-4" />
              </button>
              <button
                onClick={() => setThemeValue("dark")}
                className={`flex-1 p-1.5 rounded-md transition-all flex items-center justify-center gap-1 ${theme === "dark" ? "bg-background shadow-md text-foreground ring-1 ring-border dark:bg-muted-foreground/20 dark:ring-white/10" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"}`}
                aria-label="Dark Theme"
              >
                <Moon className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="h-px bg-border -mx-2" />

          {/* Language Section */}
          <div>
            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 mb-1">
              <Globe className="w-3.5 h-3.5" /> {dict["nav.language"]}
            </div>
            <div className={languageListClasses}>
              {i18n.locales.map((locale) => (
                <button
                  key={locale}
                  onClick={() => handleLangSelect(locale)}
                  aria-pressed={locale === currentLang}
                >
                  <span>{languageNames[locale]}</span>
                  {locale === currentLang && (
                    <Check className="w-4 h-4 text-primary" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
